import Logger from 'core/utils/Logger'
import type { KeyInitiative } from 'partner360/entities/keyInitiatives'
import {
  getPlanField,
  getPrevYearActualsField,
  getPrevYearPlanField,
} from 'partner360/features/keyInitiatives/lib/financialFieldHelper'
import type { TKeyInitiativesFinancialData } from 'partner360/features/keyInitiatives/model/financialData'
import {
  getGoalField,
  getInitiativeIncrementalField,
  TGoalField,
  TIncrementalField,
} from 'partner360/features/keyInitiatives/ui/helpers/fieldHelper'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { IKeyInitiativeForm } from '../models/keyInitiativeForm'

const MINIMUM_GOAL_TO_PLAN_RATIO = 0.07

export const finSobValidation = (
  formData: IKeyInitiativeForm,
  finSobData: TKeyInitiativesFinancialData[],
  goalFieldName: string,
  otherInitiatives: readonly Pick<KeyInitiative, TGoalField | TIncrementalField>[] = []
) => {
  if (!formData || !finSobData || finSobData.length === 0) {
    return {
      isWarning: false,
      isError: false,
      isGoalBelowMinimum: false,
      planValue: 0,
      originalPlanValue: 0,
    }
  }

  const finSob = finSobData[0]

  const valueType = Object.values(ActualsValueTypes).find(
    (type) => type !== ActualsValueTypes.None && type === formData.valueType
  )
  const currentPlan = valueType ? (finSob[getPlanField(valueType, formData.currency)] ?? 0) : 0
  const isIncrementalGoal = goalFieldName.startsWith('incremental')
  const previousActuals = valueType
    ? finSob[getPrevYearActualsField(valueType, formData.currency)]
    : undefined
  const previousPlan = valueType
    ? finSob[getPrevYearPlanField(valueType, formData.currency)]
    : undefined
  const baseline = previousActuals ?? previousPlan ?? 0
  const originalPlanValue = isIncrementalGoal ? currentPlan - baseline : currentPlan
  const coveredField = valueType
    ? isIncrementalGoal
      ? getInitiativeIncrementalField(valueType, formData.currency)
      : getGoalField(valueType, formData.currency)
    : undefined
  const coveredPlanValue = coveredField
    ? otherInitiatives.reduce((sum, initiative) => {
        const value = initiative[coveredField]
        return sum + (typeof value === 'number' && Number.isFinite(value) ? value : 0)
      }, 0)
    : 0
  // Keep the actual difference, including over-coverage; never subtract the edited initiative.
  const planValue = originalPlanValue - coveredPlanValue

  const goalValue = Number(formData?.[goalFieldName] || 0)
  if (!goalValue) {
    return {
      isWarning: false,
      isError: false,
      isGoalBelowMinimum: false,
      planValue,
      originalPlanValue,
    }
  }

  Logger.log(`${goalFieldName}: ${goalValue}, compareTo: ${planValue}`)

  // A non-positive leftover is not a meaningful comparison target (including over-coverage).
  // Required/positive Estimated Sales validation remains independent of this plan comparison.
  if (planValue <= 0) {
    return {
      isWarning: false,
      isError: false,
      isGoalBelowMinimum: false,
      planValue,
      originalPlanValue,
    }
  }
  if (goalValue < Number(planValue)) {
    const isGoalBelowMinimum = goalValue / Number(planValue) < MINIMUM_GOAL_TO_PLAN_RATIO

    return {
      isWarning: isGoalBelowMinimum,
      isError: false,
      isGoalBelowMinimum,
      planValue,
      originalPlanValue,
    }
  }

  const diff = Math.abs(calcPercentage(planValue, goalValue) ?? 0)
  const isWarning = diff >= 20
  const isError = diff >= 30

  if (!goalValue || !planValue) {
    return { isWarning, isError, isGoalBelowMinimum: false, planValue, originalPlanValue }
  }

  return { isWarning, isError, isGoalBelowMinimum: false, planValue, originalPlanValue }
}

const calcPercentage = (value1: number = 0, value2: number = 0): number | undefined => {
  if (value1 === null || !value1) {
    return undefined
  }
  if (value2 === null || !value2) {
    return undefined
  }
  if (value1 === 0 && value2 > 0) {
    return 100
  }
  if (value1 === 0) {
    return 0
  }
  if (value1 >= 0 && value2 === 0) {
    return -100
  }

  return ((value2 - value1) / Math.abs(value1)) * 100
}
